// src/components/admin/LogsPanel.js
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AlertTriangle, AlertOctagon, Info,
  Search, RefreshCw, Filter, ChevronLeft, ChevronRight,
  X, Clock, User, Cpu, Wifi, Key, CreditCard, Webhook,
  Shield, RotateCcw,
} from 'lucide-react';
import { fetchLogs, fetchLogStats } from '../../services/logService';

// ── Constants ─────────────────────────────────────────────────────────────────

const LEVELS = [
  { value: '',         label: 'All Levels' },
  { value: 'info',     label: 'Info' },
  { value: 'warning',  label: 'Warning' },
  { value: 'error',    label: 'Error' },
  { value: 'critical', label: 'Critical' },
];

const CATEGORIES = [
  { value: '',          label: 'All Categories' },
  { value: 'auth',      label: 'Auth' },
  { value: 'user_mgmt', label: 'User Mgmt' },
  { value: 'device',    label: 'Devices' },
  { value: 'gateway',   label: 'Gateways' },
  { value: 'api_key',   label: 'API Keys' },
  { value: 'payment',   label: 'Payments' },
  { value: 'webhook',   label: 'Webhook' },
  { value: 'system',    label: 'System' },
];

const DATE_PRESETS = [
  { value: '',     label: 'All Time' },
  { value: '1h',   label: 'Last 1 hour' },
  { value: '24h',  label: 'Last 24 hours' },
  { value: '7d',   label: 'Last 7 days' },
  { value: '30d',  label: 'Last 30 days' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const levelConfig = {
  info:     { color: 'bg-blue-100 text-blue-700',    dot: 'bg-blue-400',    Icon: Info },
  warning:  { color: 'bg-amber-100 text-amber-700',  dot: 'bg-amber-400',   Icon: AlertTriangle },
  error:    { color: 'bg-red-100 text-red-700',      dot: 'bg-red-500',     Icon: AlertOctagon },
  critical: { color: 'bg-rose-100 text-rose-800 font-semibold', dot: 'bg-rose-600', Icon: AlertOctagon },
};

const categoryConfig = {
  auth:      { color: 'bg-purple-100 text-purple-700', Icon: Shield },
  user_mgmt: { color: 'bg-indigo-100 text-indigo-700', Icon: User },
  device:    { color: 'bg-teal-100 text-teal-700',     Icon: Wifi },
  gateway:   { color: 'bg-cyan-100 text-cyan-700',     Icon: Cpu },
  api_key:   { color: 'bg-orange-100 text-orange-700', Icon: Key },
  payment:   { color: 'bg-green-100 text-green-700',   Icon: CreditCard },
  webhook:   { color: 'bg-pink-100 text-pink-700',     Icon: Webhook },
  system:    { color: 'bg-gray-100 text-gray-600',     Icon: Cpu },
};

const presetToRange = (preset) => {
  if (!preset) return { from: '', to: '' };
  const now  = new Date();
  const from = new Date(now);
  if (preset === '1h')  from.setHours(from.getHours() - 1);
  if (preset === '24h') from.setDate(from.getDate() - 1);
  if (preset === '7d')  from.setDate(from.getDate() - 7);
  if (preset === '30d') from.setDate(from.getDate() - 30);
  return { from: from.toISOString(), to: now.toISOString() };
};

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

// ── Sub-components ────────────────────────────────────────────────────────────

const LevelBadge = ({ level }) => {
  const cfg = levelConfig[level] || levelConfig.info;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />{level}
    </span>
  );
};

const CategoryBadge = ({ category }) => {
  const cfg = categoryConfig[category] || categoryConfig.system;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />{category?.replace('_', ' ')}
    </span>
  );
};

const StatCard = ({ label, value, color, Icon }) => (
  <div className={`rounded-xl border p-4 flex items-center gap-3 ${color}`}>
    <Icon className="w-5 h-5 shrink-0" />
    <div>
      <p className="text-2xl font-bold leading-none">{value ?? '—'}</p>
      <p className="text-xs mt-0.5 opacity-80">{label}</p>
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const LogsPanel = () => {
  // filters
  const [level,      setLevel]      = useState('');
  const [category,   setCategory]   = useState('');
  const [q,          setQ]          = useState('');
  const [qInput,     setQInput]     = useState('');
  const [datePreset, setDatePreset] = useState('24h');
  const [page,       setPage]       = useState(1);

  // data
  const [logs,       setLogs]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats,      setStats]      = useState(null);
  const [loading,    setLoading]    = useState(true);

  // auto-refresh
  const [autoRefresh,   setAutoRefresh]   = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const autoTimer = useRef(null);

  // expanded row
  const [expandedId, setExpandedId] = useState(null);

  const PAGE_SIZE = 50;

  // ── Load logs ───────────────────────────────────────────────────────────────
  const loadLogs = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const { from, to } = presetToRange(datePreset);
      const res = await fetchLogs({ level, category, q, from, to, page: pg, limit: PAGE_SIZE });
      setLogs(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
      setPage(pg);
      setLastRefreshed(new Date());
    } catch (e) {
      console.error('LogsPanel fetchLogs:', e);
    } finally {
      setLoading(false);
    }
  }, [level, category, q, datePreset]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetchLogStats();
      setStats(res);
    } catch (e) {
      console.error('LogsPanel fetchLogStats:', e);
    }
  }, []);

  // initial + filter changes
  useEffect(() => { loadLogs(1); }, [loadLogs]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // auto-refresh every 30 s when enabled
  useEffect(() => {
    if (autoRefresh) {
      autoTimer.current = setInterval(() => { loadLogs(page); loadStats(); }, 30000);
    } else {
      clearInterval(autoTimer.current);
    }
    return () => clearInterval(autoTimer.current);
  }, [autoRefresh, loadLogs, loadStats, page]);

  // search debounce
  const searchTimer = useRef(null);
  const handleSearchChange = (v) => {
    setQInput(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setQ(v), 400);
  };

  const clearFilters = () => {
    setLevel(''); setCategory(''); setQ(''); setQInput(''); setDatePreset('24h');
  };
  const hasFilters = level || category || q || datePreset !== '24h';

  // ── Stat values ─────────────────────────────────────────────────────────────
  const s24 = stats?.last24h?.byLevel || {};
  const statCards = [
    { label: 'Total (24 h)',    value: stats?.last24h?.total,    color: 'bg-gray-50 border-gray-200 text-gray-700',     Icon: Info },
    { label: 'Info (24 h)',     value: s24.info    || 0,          color: 'bg-blue-50 border-blue-200 text-blue-700',     Icon: Info },
    { label: 'Warnings (24 h)', value: s24.warning  || 0,         color: 'bg-amber-50 border-amber-200 text-amber-700',  Icon: AlertTriangle },
    { label: 'Errors (24 h)',   value: s24.error    || 0,         color: 'bg-red-50 border-red-200 text-red-700',        Icon: AlertOctagon },
    { label: 'Critical (24 h)', value: s24.critical || 0,         color: 'bg-rose-50 border-rose-200 text-rose-800',     Icon: AlertOctagon },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* ── Title bar ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">System Logs</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Activity, errors and security events — retained for 90 days
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastRefreshed && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {lastRefreshed.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            onClick={() => setAutoRefresh(v => !v)}
            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border font-medium transition-colors
              ${autoRefresh ? 'bg-green-50 border-green-300 text-green-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >
            <RotateCcw className={`w-3.5 h-3.5 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto (30 s)' : 'Auto-refresh'}
          </button>
          <button
            onClick={() => { loadLogs(page); loadStats(); }}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
          >
            <RefreshCw className="w-3.5 h-3.5" />Refresh
          </button>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {statCards.map(c => (
          <StatCard key={c.label} {...c} />
        ))}
      </div>

      {/* ── Recent critical/error strip ── */}
      {stats?.recentErrors?.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 space-y-1.5">
          <p className="text-xs font-semibold text-rose-700 uppercase tracking-wide flex items-center gap-1.5">
            <AlertOctagon className="w-3.5 h-3.5" />Recent errors &amp; criticals
          </p>
          {stats.recentErrors.map(e => (
            <div key={e._id} className="flex items-start gap-2 text-sm text-rose-800">
              <span className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${e.level === 'critical' ? 'bg-rose-600' : 'bg-red-400'}`} />
              <span className="truncate flex-1">{e.message}</span>
              <span className="text-xs text-rose-500 shrink-0">{fmt(e.createdAt)}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Filter bar ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={qInput}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="Search messages, emails, actions…"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>

          {/* Level filter */}
          <select value={level} onChange={e => { setLevel(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white">
            {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>

          {/* Category filter */}
          <select value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white">
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>

          {/* Date preset */}
          <select value={datePreset} onChange={e => { setDatePreset(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white">
            {DATE_PRESETS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>

          {hasFilters && (
            <button onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 px-2 py-2">
              <X className="w-3.5 h-3.5" />Clear
            </button>
          )}
        </div>

        {/* Active filter chips */}
        <div className="flex flex-wrap gap-2 text-xs">
          {level    && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Filter className="w-3 h-3" />Level: {level}</span>}
          {category && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Filter className="w-3 h-3" />Category: {category.replace('_', ' ')}</span>}
          {q        && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex items-center gap-1"><Search className="w-3 h-3" />"{q}"</span>}
        </div>
      </div>

      {/* ── Log table ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">

        {/* Table header */}
        <div className="grid grid-cols-[140px_90px_110px_1fr_160px] gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <span>Timestamp</span>
          <span>Level</span>
          <span>Category</span>
          <span>Message</span>
          <span className="hidden lg:block">Actor</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />Loading logs…
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Info className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm">No logs match the current filters</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {logs.map(log => {
              const isExpanded = expandedId === log._id;
              return (
                <div key={log._id}>
                  {/* Row */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : log._id)}
                    className={`w-full grid grid-cols-[140px_90px_110px_1fr_160px] gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors
                      ${log.level === 'critical' ? 'bg-rose-50/60' : log.level === 'error' ? 'bg-red-50/40' : ''}`}
                  >
                    {/* Timestamp */}
                    <span className="text-xs text-gray-500 font-mono leading-relaxed pt-0.5">
                      {fmt(log.createdAt)}
                    </span>

                    {/* Level */}
                    <span className="pt-0.5"><LevelBadge level={log.level} /></span>

                    {/* Category */}
                    <span className="pt-0.5"><CategoryBadge category={log.category} /></span>

                    {/* Message + action */}
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 truncate">{log.message}</p>
                      <p className="text-xs text-gray-400 font-mono">{log.action}</p>
                    </div>

                    {/* Actor */}
                    <span className="hidden lg:block text-xs text-gray-500 truncate pt-0.5">
                      {log.actorEmail || <span className="italic text-gray-300">system</span>}
                    </span>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-gray-50 border-t border-dashed border-gray-200">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-3 text-sm">
                        <Field label="Action"    value={log.action} mono />
                        <Field label="Level"     value={log.level} />
                        <Field label="Category"  value={log.category?.replace('_', ' ')} />
                        <Field label="Actor"     value={log.actorEmail || 'system'} />
                        <Field label="Role"      value={log.actorRole || '—'} />
                        <Field label="Target"    value={log.targetEmail || log.targetId || '—'} />
                        <Field label="Target type" value={log.targetType || '—'} />
                        <Field label="IP"        value={log.ip || '—'} mono />
                        <Field label="Timestamp" value={log.createdAt ? new Date(log.createdAt).toISOString() : '—'} mono />
                      </div>
                      {log.meta && Object.keys(log.meta).length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Meta</p>
                          <pre className="bg-white border border-gray-200 rounded-lg p-3 text-xs text-gray-700 overflow-x-auto whitespace-pre-wrap">
                            {JSON.stringify(log.meta, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              {total.toLocaleString()} total · page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => loadLogs(page - 1)} disabled={page <= 1}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" />
              </button>
              {/* Page number buttons — show max 5 around current */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) => p === '…'
                  ? <span key={`e${i}`} className="px-1 text-gray-400 text-xs">…</span>
                  : <button key={p} onClick={() => loadLogs(p)}
                      className={`w-7 h-7 text-xs rounded-lg font-medium
                        ${p === page ? 'bg-purple-600 text-white' : 'hover:bg-gray-100 text-gray-600'}`}>
                      {p}
                    </button>
                )}
              <button onClick={() => loadLogs(page + 1)} disabled={page >= totalPages}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Small labelled field for the expanded row detail
const Field = ({ label, value, mono }) => (
  <div>
    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
    <p className={`text-sm text-gray-800 break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
  </div>
);

export default LogsPanel;
